import React, { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from '../../../Flux/index.js'
import { visualizationSlice } from '../../../Flux/Slice/Visualization/VisualizationSlice.js'
import { SINK_REGISTRY } from 'View/Component/ConfigurableVisualization/Sinks/SinkRegistry.js'
import { useSourceView } from 'View/Component/ConfigurableVisualization/Sinks/UseSourceView.js'
import type { VizDirective } from '../../../Generated/VizDirective.ts'
import { CUBE_SINK_BY_KIND, chatSinkConfig, chatSourceConfig, chatSourceShared } from './chatCube.js'
import { cubeSourceId } from '../../../Flux/Slice/Chat/chatViz.js'
import { chartItemId } from '../../../Flux/Slice/Chat/workspaceItems.js'
import { selectVizSamples } from '../../../Flux/Slice/Visualization/SelectVisualizationSamples.js'
import { useTransformer } from '../../Component/ConfigurableVisualization/Transformer/UseTransformer.tsx'
import { resolveTaxonEntryPath } from './resolveTaxonEntry.js'

const EMPTY_PATH: string[] = []

/**
 * Zooms a chat cube source to a taxon the agent named by NAME (`directive.taxonEntry`) rather than by
 * a concrete lineage. Rendered only once the source's cube is resident (parent gates on `dataReady`)
 * so the taxa index — the same one the workspace search panel builds — has rows to resolve against.
 * Resolves the name → lineage `path` and dispatches the shared `setTaxaZoom`; `taxaPathRepairEpic`
 * then reconciles it on any later cube change, exactly as it does for a click in the search panel.
 * A miss (or an empty index) leaves the path at root — the pre-existing behavior.
 */
function TaxonEntryZoom({ sourceId, entry }: { sourceId: string; entry: string }) {
  const dispatch = useDispatch()
  const { shared } = useSourceView(sourceId)
  const indexRes = useTransformer(sourceId, undefined, 'taxaNavigatorIndex', {
    selectedPath: shared?.selectedPath ?? EMPTY_PATH,
    gradeThreshold: shared?.gradeThreshold ?? 0
  })
  // Resolve the requested name → lineage once the index is resident. `null` = still loading; `[]` = the
  // index is ready but the name matched nothing (a genuine miss).
  const path = useMemo(
    () => (indexRes.status === 'ready' ? resolveTaxonEntryPath(indexRes.value, entry) : null),
    [indexRes, entry]
  )
  const zoomedRef = useRef(false)
  useEffect(() => {
    zoomedRef.current = false
  }, [sourceId, entry])
  useEffect(() => {
    if (zoomedRef.current || path === null) return
    zoomedRef.current = true
    if (path.length > 0) dispatch(visualizationSlice.actions.setTaxaZoom({ sourceId, path }))
  }, [path, sourceId, dispatch])
  // Safeguard (#63): never silently sit at root when the agent asked to focus a taxon that isn't here.
  // Surface the miss so the user (and, by reading it back, the agent) knows the focus was NOT applied.
  if (path !== null && path.length === 0) {
    return (
      <div className="mb-1 rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-700" role="status">
        Couldn’t find “{entry}” in this dataset — showing the full scope instead. Try a taxon that appears here.
      </div>
    )
  }
  return null
}

/**
 * Renders a cube VizDirective by driving the (singleton) workspace VisualizationSlice ADDITIVELY: we add
 * our own source + sink (unique ids) — never `loadSavedViz`, which would wipe sibling sources — then mount
 * the real workspace sink component once the source reaches `dataReady`. Multiple chat cube bubbles thus
 * coexist in the one workspace. The per-scope sample bootstrap (reset/setScope/sample-loads) is done once
 * by ChatPage; we only add our source after the scope is set.
 *
 * v1 limits (flagged): one source per bubble (one bucket-per-sample pipeline), no multi-bucket sample
 * selection, no SPIN. Extend via the directive + shared state as needed.
 */
export function ChatCubeChart({ directive }: { directive: VizDirective }) {
  const dispatch = useDispatch()
  const sinkKey = CUBE_SINK_BY_KIND[directive.chartKind]

  // Stable id derived from the directive's identity, so an identical chart reuses one workspace source.
  const sourceId = cubeSourceId(directive)
  const sinkId = `${sourceId}-sink`
  // Per-sink tuning: start from the registry default config, then merge the directive's validated sinkParams.
  const sinkConfig = useMemo(
    () => (sinkKey ? chatSinkConfig(SINK_REGISTRY[sinkKey].makeConfig(sinkId), directive) : undefined),
    [sinkKey, sinkId, directive]
  )
  // Single-bucket sinks (pie/treemap) show ONE bucket — use an aggregate (no-groupBy) pipeline so that bucket
  // is the whole scope, not just sample #1. Multi-bucket sinks keep the per-sample buckets.
  const isSingleBucket = !!sinkKey && SINK_REGISTRY[sinkKey].renderMode === 'single-bucket'

  // The ChatPage bootstrap sets the workspace scope; only add our source once it matches (else the source
  // would have no scope and never fetch).
  const scopeReady = useSelector((s) => s.visualization.scope?.id === directive.scopeId)
  // AND wait until samples are actually resolvable (selectVizSamples needs BOTH the order object and its
  // samplePools — or project samples+orders). BlockingSourceEpic's gate checks only samplePools, so creating
  // the source too early buckets 0 samples and never re-buckets, leaving the chart permanently empty.
  const samplesReady = useSelector((s) => selectVizSamples(s).length > 0)

  // Persisted pinned view (if the user pinned this chart and the workspace was reloaded): seeds the recreated
  // source so the chart comes back at the pinned view — and "restore to pinned" works — before any re-pin this
  // session. In-session this is undefined for a fresh chart; the live source's own nav is preserved by
  // createSource's "existing wins".
  const pinnedView = useSelector((s) => s.chat.itemMeta[chartItemId(directive)]?.pinnedView)

  const createdRef = useRef(false)
  useEffect(() => {
    if (!sinkKey || !sinkConfig || !scopeReady || !samplesReady || createdRef.current) return
    createdRef.current = true
    dispatch(
      visualizationSlice.actions.createSource({
        sourceId,
        initial: {
          name: directive.title ?? sinkKey,
          config: chatSourceConfig(directive, isSingleBucket),
          shared: pinnedView ?? chatSourceShared(directive),
          pinnedShared: pinnedView
        }
      })
    )
    dispatch(visualizationSlice.actions.registerSink({ sourceId, sinkconfig: sinkConfig }))
    // A persisted pinned view already carries the right selectedPath; only apply the directive's zoom otherwise.
    if (!pinnedView && directive.selectedPath && directive.selectedPath.length > 0) {
      dispatch(visualizationSlice.actions.setTaxaZoom({ sourceId, path: directive.selectedPath }))
    }
    return () => {
      createdRef.current = false
      dispatch(visualizationSlice.actions.unregisterSink({ sourceId, sinkId }))
    }
  }, [sinkKey, sinkConfig, isSingleBucket, scopeReady, samplesReady, sourceId, sinkId, dispatch, directive, pinnedView])

  const { status, snapshot } = useSourceView(sourceId)

  if (!sinkKey || !sinkConfig) {
    return <div className="text-xs text-gray-500">Unsupported chart: {directive.chartKind}</div>
  }
  if (!scopeReady || status !== 'dataReady') {
    return (
      <div className="flex h-[440px] items-center justify-center text-xs text-gray-500">
        <span className="loading loading-dots" /> preparing chart…
      </div>
    )
  }

  const entry = SINK_REGISTRY[sinkKey]
  const SinkComponent = entry.component
  // Aggregate (single-bucket) sources yield one "all" bucket; selectedBuckets defaults to all buckets.
  const bucketKey = snapshot?.selectedBuckets?.[0]?.key
  if (entry.renderMode === 'single-bucket' && !bucketKey) {
    return <div className="flex h-[440px] items-center justify-center text-xs text-gray-500">No data to chart.</div>
  }

  // Name-based taxon focus: resolve `taxonEntry` → lineage now that the cube is resident. An explicit
  // `selectedPath` (dispatched at source creation) always wins. SPIN cubes carry a taxonomy ladder too
  // (the treemap/pie render taxa off them), so they zoom the same way; a cube that genuinely has no
  // `taxa` column yields an empty index (see selectTaxaNavigatorIndex) and simply stays at root.
  const wantTaxonEntry =
    !!directive.taxonEntry && !(directive.selectedPath && directive.selectedPath.length > 0)

  // Sinks are `h-full` — give them a definite, generous height or the chart canvas is clipped.
  return (
    <div className="h-[440px] w-full">
      {wantTaxonEntry && <TaxonEntryZoom sourceId={sourceId} entry={directive.taxonEntry!} />}
      <SinkComponent sourceId={sourceId} sinkId={sinkId} config={sinkConfig} bucketKey={bucketKey} />
    </div>
  )
}
